import { useCallback } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import Accordion from 'src/components/Accordion';
import { useGetRecommendedHashtagsQuery } from 'src/hooks/useProfileQuery';
import { FieldIconHashtag } from './styles';
const StyledAccordion = styled(Accordion)`
  border-radius: 8px;
  background-color: ${({
  theme
}) => theme.color.surface4};
`;
const AccordionIcon = styled(Accordion.Icon)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;
const StyledAccordionHeading = styled(Accordion.Heading)<{
  hasHashtags: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 16px;

  ${({
  hasHashtags
}) => hasHashtags && `
      align-items: start;
      ${FieldIconHashtag} {
        margin-top: 6px;
      }
      ${AccordionIcon} {
        margin-top: 8px;
      }
  `};
`;
const HeadingContent = styled.div`
  flex: 1;
`;
const HashtagPlaceholder = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  color: ${({
  theme
}) => theme.color.content8};
  flex: 1;
`;
const HashtagList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 10px;
  flex: 1;
`;
const HashtagChip = styled.label<{
  active: boolean;
}>`
  display: inline-block;
  padding: 5.5px 16px;
  background-color: ${({
  theme
}) => theme.color.surface5};
  border-radius: 24px;
  font-size: 14px;
  line-height: 21px;
  color: ${({
  theme
}) => theme.color.content12};
  ${({
  active,
  theme
}) => active && `
    background-color: ${theme.color.surface10};
    color: ${theme.color.content1};
  `}
`;
const AppliedHashtag = styled(HashtagChip)`
  cursor: unset;
`;
const SelectableHashtag = styled(HashtagChip)`
  cursor: pointer;
`;
const PanelInner = styled.div`
  border-top: 1px solid ${({
  theme
}) => theme.color.surface5};
  padding: 4px;
`;
const SelectableHashtagList = styled(HashtagList)`
  max-height: 182px;
  overflow-y: auto;
  ${({
  theme
}) => theme.scroll};
  padding: 12px;
`;
const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  width: 0px;
  height: 0px;
`;
interface Props {
  value: string[];
  onChange: (value: string[]) => void;
}
const HashtagEditor = ({
  value,
  onChange
}: Props) => {
  const {
    t
  } = useTranslation();
  const {
    data: {
      data: hashtags
    } = {}
  } = useGetRecommendedHashtagsQuery();
  const handleHashtagCheck = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      onChange(value.concat([event.target.value]).slice(-30));
    } else {
      onChange(value.filter(hashtag => hashtag !== event.target.value));
    }
  }, [value, onChange]);
  const hasAppliedHashtags = value.length > 0;
  return <StyledAccordion closeWhenClickOutside toggleTrigger='heading' data-sentry-element="StyledAccordion" data-sentry-component="HashtagEditor" data-sentry-source-file="HashtagEditor.tsx">
      <StyledAccordionHeading hasHashtags={hasAppliedHashtags} data-sentry-element="StyledAccordionHeading" data-sentry-source-file="HashtagEditor.tsx">
        <FieldIconHashtag data-sentry-element="FieldIconHashtag" data-sentry-source-file="HashtagEditor.tsx" />
        <HeadingContent data-sentry-element="HeadingContent" data-sentry-source-file="HashtagEditor.tsx">
          {hasAppliedHashtags ? <HashtagList>
              {value.map(hashtag => <AppliedHashtag active key={`Hashtag-Active-${hashtag}`} htmlFor='HashtagFieldSelector'>
                  {hashtag}
                </AppliedHashtag>)}
            </HashtagList> : <HashtagPlaceholder>{t('PROFILE_HASHTAG_DESC')}</HashtagPlaceholder>}
        </HeadingContent>
        <AccordionIcon data-sentry-element="AccordionIcon" data-sentry-source-file="HashtagEditor.tsx" />
      </StyledAccordionHeading>
      <Accordion.Panel data-sentry-element="Accordion.Panel" data-sentry-source-file="HashtagEditor.tsx">
        <PanelInner data-sentry-element="PanelInner" data-sentry-source-file="HashtagEditor.tsx">
          <SelectableHashtagList data-sentry-element="SelectableHashtagList" data-sentry-source-file="HashtagEditor.tsx">
            {hashtags?.map(({
            hashtag,
            id
          }) => {
            const inputId = `HashtagChip-${id}`;
            const checked = value.includes(hashtag);
            return <SelectableHashtag active={checked} key={id} htmlFor={inputId}>
                  {hashtag}
                  <HiddenInput inputMode='none' type='checkbox' onChange={handleHashtagCheck} id={inputId} value={hashtag} checked={checked} />
                </SelectableHashtag>;
          })}
          </SelectableHashtagList>
        </PanelInner>
      </Accordion.Panel>
    </StyledAccordion>;
};
export default HashtagEditor;